import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { Table, DatePicker, Select, Descriptions, Modal } from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { AuthContext } from "../../context/AuthContext";

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default function AppliedOnDuty() {
    const { authData, backUrl } = useContext(AuthContext);

    const [requests, setRequests] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [filters, setFilters] = useState({
        status: null,
        month: null,
        year: null,
        fromDate: null,
        toDate: null,
    });


    const [showLead, setShowLead] = useState(false);
    const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchData = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/hr/attendance/my-onduty-requests/`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });
            setRequests(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching On Duty requests:", error.response || error.message);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const statusColors = {
        PENDING: "#faad14",
        APPROVED: "green",
        REJECTED: "red",
    };

    const getStatusColor = (status) => {
        if (!status) return "#faad14";
        const upper = status.toUpperCase();
        return statusColors[upper] || "#faad14";
    };

    const columns = [
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            key: "employee_name",
        },
        {
            title: "On Duty Type",
            dataIndex: "onduty_type",
            key: "onduty_type",
            render: (text, record) =>
                <>
                    <p>
                        {text}
                    </p>
                    <p>
                        {record.check_in_out_mode}
                    </p>
                    {record?.lead_data && (
                        <a
                            onClick={() => {
                                setSelectedLeadDetails(record.lead_data);
                                setShowLead(true);
                            }}
                            style={{ color: "#1677ff", cursor: "pointer" }}
                        >
                            Lead Details
                        </a>
                    )}
                </>
        },
        {
            title: "Check-In Photo",
            dataIndex: "photo",
            key: "check_in_photo",
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-In"
                        style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
                    />
                ) : (
                    "-"
                ),
        },
        {
            title: "Location",
            dataIndex: "location",
            key: "location",
            width: 250,
            render: (text, record) =>
                ((record.check_in_latitude && record.check_in_longitude) || (record.check_out_latitude && record.check_out_longitude)) ? (
                    <a
                        href={`https://www.google.com/maps?q=${record.check_in_latitude || record.check_out_latitude},${record.check_in_longitude || record.check_out_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1677ff" }}
                    >
                        {text || "View Location"}
                    </a>
                ) : (
                    text || "-"
                ),
        },
        {
            title: "Start Time",
            dataIndex: "start_time",
            key: "start_time",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "End Time",
            dataIndex: "end_time",
            key: "end_time",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Remarks",
            dataIndex: "remarks",
            key: "remarks",
            width: 300,
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "300px" }}>
                    {text || "-"}
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => (
                <span style={{ fontWeight: 500, color: getStatusColor(text) }}>
                    {text
                        ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
                        : "-"}
                </span>
            ),
        },
        {
            title: "Approval Logs",
            key: "approval_logs",
            width: 400,
            render: (_, record) => {
                const logs = record.approval_logs || [];
                if (!logs.length) return "-";

                return (
                    <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "400px" }}>
                        {logs.map((log, index) => (
                            <div
                                key={index}
                                style={{
                                    borderBottom: index < logs.length - 1 ? "1px solid #ddd" : "none",
                                    paddingBottom: "6px",
                                    marginBottom: "6px",
                                }}
                            >
                                <strong>
                                    {log.approver_name}:
                                </strong>{" "}
                                <span
                                    style={{
                                        color: getStatusColor(log.status),
                                        fontWeight: 500,
                                    }}
                                >
                                    {log.status
                                        ? log.status.charAt(0).toUpperCase() +
                                        log.status.slice(1).toLowerCase()
                                        : "-"}
                                </span>
                                <br />
                                {log.remarks && (
                                    <>
                                        <span style={{ color: "#666" }}>Remarks: {log.remarks}</span>
                                        <br />
                                    </>
                                )}
                                {log.updated_at && (
                                    <small style={{ color: "#999" }}>
                                        {dayjs(log.updated_at).format("DD/MM/YYYY hh:mm A")}
                                    </small>
                                )}
                            </div>
                        ))}
                    </div>
                );
            },
        },
    ];

    // ✅ Filter Logic
    const filteredData = requests.filter((item) => {
        const startDate = dayjs(item.start_time);

        const matchStatus = filters.status
            ? item.status?.toUpperCase() === filters.status.toUpperCase()
            : true;

        const matchMonth = filters.month ? startDate.month() + 1 === filters.month : true;
        const matchYear = filters.year ? startDate.year() === filters.year : true;

        const matchDateRange =
            filters.fromDate && filters.toDate
                ? startDate.isBetween(filters.fromDate, filters.toDate, "day", "[]")
                : filters.fromDate
                    ? startDate.isSameOrAfter(filters.fromDate, "day")
                    : filters.toDate
                        ? startDate.isSameOrBefore(filters.toDate, "day")
                        : true;
        return matchStatus && matchMonth && matchYear && matchDateRange;
    });

    // ✅ Extract unique years
    const years = Array.from(
        new Set(requests.map((item) => dayjs(item.start_time).year()))
    ).sort((a, b) => b - a);

    return (
        <>
            {showLead && (
                <Modal
                    open={showLead}
                    footer={null}
                    onCancel={() => {
                        setShowLead(false);
                        setSelectedLeadDetails(null);
                    }}
                    width={700}
                >
                    {selectedLeadDetails !== null ? (
                        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                            <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
                                <Descriptions.Item label="Lead Number">
                                    <strong>{selectedLeadDetails.lead_no || "-"}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="Customer Name">
                                    {selectedLeadDetails.customer_name || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Email">
                                    {selectedLeadDetails.email || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Phone Number">
                                    {selectedLeadDetails.number || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Project Name">
                                    {selectedLeadDetails.project_name || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <span
                                        style={{
                                            padding: "4px 12px",
                                            borderRadius: "4px",
                                            backgroundColor: selectedLeadDetails.status
                                                ?.toLowerCase()
                                                .includes("cancel")
                                                ? "#fff2e8"
                                                : "#f6ffed",
                                            color: selectedLeadDetails.status?.toLowerCase().includes("cancel")
                                                ? "#d46b08"
                                                : "#52c41a",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {selectedLeadDetails.status || "-"}
                                    </span>
                                </Descriptions.Item>
                            </Descriptions>

                        </div>
                    ) : (
                        <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                            No lead details available
                        </div>
                    )}
                </Modal>
            )}

            {/* Filters */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10px",
                    marginBottom: "16px",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: "center",
                }}
            >
                <Select
                    placeholder="Filter by Year"
                    style={{ width: isMobile ? "100%" : 140 }}
                    allowClear
                    onChange={(value) => setFilters({ ...filters, year: value })}
                >
                    {years.map((year) => (
                        <Select.Option key={year} value={year}>
                            {year}
                        </Select.Option>
                    ))}
                </Select>

                <Select
                    placeholder="Filter by Month"
                    style={{ width: isMobile ? "100%" : 160 }}
                    allowClear
                    onChange={(value) => setFilters({ ...filters, month: value })}
                >
                    {dayjs.months().map((month, index) => (
                        <Select.Option key={index} value={index + 1}>
                            {month}
                        </Select.Option>
                    ))}
                </Select>

                <DatePicker
                    placeholder="From Date"
                    format="DD/MM/YYYY"
                    style={{ width: isMobile ? "100%" : 160 }}
                    onChange={(date) => setFilters({ ...filters, fromDate: date })}
                />

                <DatePicker
                    placeholder="To Date"
                    format="DD/MM/YYYY"
                    style={{ width: isMobile ? "100%" : 160 }}
                    onChange={(date) => setFilters({ ...filters, toDate: date })}
                />

                <Select
                    placeholder="Status"
                    style={{ width: isMobile ? "100%" : 200 }}
                    allowClear
                    onChange={(value) => setFilters({ ...filters, status: value })}
                >
                    {["PENDING", "APPROVED", "REJECTED"].map((status) => (
                        <Select.Option key={status} value={status}>
                            {status.charAt(0) + status.slice(1).toLowerCase()}
                        </Select.Option>
                    ))}
                </Select>
            </div>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={filteredData.map((item, index) => ({
                    key: index,
                    ...item,
                }))}
                pagination={{ pageSize: 20 }}
                scroll={{ x: true }}
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
                bordered
            />
        </>
    );
}